import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query
} from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes
} from 'firebase/storage';
import { db, storage } from '../firebase';
import Snackbar from '../components/Snackbar';
import { useSnackbar } from '../hooks/useSnackbar';
import './CustomVideoFramePage.css';

const CustomVideoFramePage = () => {
  const { phoneNumber } = useParams();
  const navigate = useNavigate();
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const userId = useMemo(() => phoneNumber?.trim() || '', [phoneNumber]);

  const fileInputRef = useRef(null);

  const [videoFrames, setVideoFrames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [frameToDelete, setFrameToDelete] = useState(null);

  const fetchVideoFrames = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);
      let framesSnapshot;
      try {
        const framesQuery = query(
          collection(db, 'users', userId, 'customVideoFrames'),
          orderBy('createdAt', 'desc')
        );
        framesSnapshot = await getDocs(framesQuery);
      } catch (orderError) {
        
        framesSnapshot = await getDocs(collection(db, 'users', userId, 'customVideoFrames'));
      }

      const items = [];
      framesSnapshot.forEach((frameDoc) => {
        const data = frameDoc.data();
        items.push({
          id: frameDoc.id,
          ...data,
          createdAt: data.createdAt || new Date().toISOString()
        });
      });

      items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setVideoFrames(items);
    } catch (error) {
      console.error('Error fetching custom video frames:', error);
      setVideoFrames([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      navigate('/admin/users');
      return;
    }

    const loadUserDetails = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserName(data.name || data.fullName || data.displayName || userId);
        } else {
          setUserName(userId);
        }
      } catch (error) {
        
        setUserName(userId);
      }
    };

    loadUserDetails();
    fetchVideoFrames();
  }, [fetchVideoFrames, navigate, userId]);

  const resetSelection = () => {
    setSelectedFile(null);
    setPreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (file) => {
    if (!file) return;

    // Check if it's a PNG file
    if (file.type !== 'image/png') {
      showSnackbar('Please select a PNG file only.', 'warning');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setPreview(event.target?.result || '');
    };
    reader.readAsDataURL(file);
    setSelectedFile(file);
  };

  const onFileInputChange = (event) => {
    const file = event.target.files?.[0];
    if (event.target) {
      event.target.value = '';
    }
    handleFileSelect(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    const file = event.dataTransfer?.files?.[0];
    handleFileSelect(file);
  };

  const handleDropZoneClick = () => {
    fileInputRef.current?.click();
  };

  const uploadVideoFrame = async () => {
    if (!selectedFile) {
      showSnackbar('Please select a PNG frame first.', 'warning');
      return;
    }
    if (!userId) {
      showSnackbar('Missing user identifier.', 'error');
      return;
    }

    try {
      setProcessing(true);
      const timestamp = Date.now();
      const storagePath = `users/${userId}/customVideoFrames/${timestamp}-${selectedFile.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, selectedFile);
      const downloadURL = await getDownloadURL(storageRef);

      const frameData = {
        url: downloadURL,
        storagePath,
        originalName: selectedFile.name,
        fileSize: selectedFile.size,
        fileType: selectedFile.type,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'users', userId, 'customVideoFrames'), frameData);
      setVideoFrames((prev) => [{ id: docRef.id, ...frameData }, ...prev]);
      resetSelection();
      showSnackbar('PNG frame uploaded successfully!', 'success');
    } catch (error) {
      console.error('Error uploading PNG frame:', error);
      showSnackbar('Failed to upload PNG frame. Please try again.', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteClick = (frame) => {
    setFrameToDelete(frame);
    setShowDeleteConfirm(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setFrameToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!frameToDelete) return;

    try {
      setProcessing(true);
      await deleteDoc(doc(db, 'users', userId, 'customVideoFrames', frameToDelete.id));
      if (frameToDelete.storagePath) {
        try {
          await deleteObject(ref(storage, frameToDelete.storagePath));
        } catch (storageError) {
          
        }
      }
      setVideoFrames((prev) => prev.filter((item) => item.id !== frameToDelete.id));
      showSnackbar('Video frame deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting video frame:', error);
      showSnackbar('Failed to delete video frame. Please try again.', 'error');
    } finally {
      setProcessing(false);
      setShowDeleteConfirm(false);
      setFrameToDelete(null);
    }
  };

  return (
    <div className="custom-video-frame-page">
      <div className="cvf-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div className="cvf-heading">
          <h1>Custom Video Frames</h1>
          <p>
            Upload and manage video frames for <strong>{userName || userId}</strong>
          </p>
        </div>
      </div>

      <div className="cvf-content">
        <div className="upload-panel">
          <h2>Upload Custom Video Frame</h2>
          <div
            className={`drag-drop-zone ${dragActive ? 'drag-over' : ''} ${preview ? 'has-preview' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleDropZoneClick}
          >
            {preview ? (
              <div className="video-preview">
                <img src={preview} alt="Preview" className="preview-video" />
              </div>
            ) : (
              <div className="drag-drop-content">
                <div className="upload-icon">🖼️</div>
                <p className="drag-drop-text">Drag &amp; drop your PNG frame here</p>
                <p className="drag-drop-subtext">or click to browse files</p>
                <div className="supported-formats">Supported format: PNG only</div>
              </div>
            )}
            <input
              type="file"
              accept="image/png,.png"
              ref={fileInputRef}
              className="file-input"
              onChange={onFileInputChange}
              hidden
            />
          </div>

          <div className="upload-actions">
            <button
              className="cancel-btn"
              onClick={resetSelection}
              disabled={!selectedFile || processing}
            >
              Clear
            </button>
            <button
              className="save-btn"
              onClick={uploadVideoFrame}
              disabled={!selectedFile || processing}
            >
              {processing ? 'Uploading...' : 'Upload PNG Frame'}
            </button>
          </div>
        </div>

        <div className="frames-section">
          <div className="frames-header">
            <h2>Existing Video Frames</h2>
            <span className="frame-count">{videoFrames.length} video frame(s)</span>
          </div>

          {loading ? (
            <div className="frames-loading">
              <div className="loading-spinner"></div>
              <p>Loading video frames...</p>
            </div>
          ) : videoFrames.length === 0 ? (
            <div className="no-frames">
              <div className="no-frames-icon">🖼️</div>
              <h3>No PNG frames uploaded yet</h3>
              <p>Upload a custom PNG frame to get started.</p>
            </div>
          ) : (
            <div className="frames-grid">
              {videoFrames.map((frame) => (
                <div key={frame.id} className="frame-card">
                  <img src={frame.url} alt={frame.originalName} className="frame-video" />
                  <div className="frame-meta">
                    <p className="frame-name">{frame.originalName || 'Custom Video Frame'}</p>
                    <p className="frame-date">Updated {new Date(frame.updatedAt || frame.createdAt).toLocaleString()}</p>
                  </div>
                  <div className="frame-actions">
                    <button
                      className="btn-danger"
                      onClick={() => handleDeleteClick(frame)}
                      disabled={processing}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="delete-confirm-modal">
            <div className="delete-modal-header">
              <h3>Confirm Delete</h3>
              <button 
                className="close-btn"
                onClick={handleDeleteCancel}
              >
                ×
              </button>
            </div>
            
            <div className="delete-modal-content">
              <div className="warning-icon">
                ⚠️
              </div>
              <p>Are you sure you want to delete this custom video frame?</p>
              <p className="delete-warning">
                This action cannot be undone.
              </p>
              <div className="frame-info">
                <strong>{frameToDelete?.originalName || 'Custom Video Frame'}</strong>
              </div>
            </div>
            
            <div className="delete-modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={handleDeleteCancel}
                disabled={processing}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
                disabled={processing}
              >
                {processing ? 'Deleting...' : 'Delete Video Frame'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar */}
      {snackbar.show && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={hideSnackbar}
        />
      )}
    </div>
  );
};

export default CustomVideoFramePage;
